<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.goods_exit')"
    :subtitle="editedItem && editedItem.id ? 'ID: ' + editedItem.id : undefined"
    icon="mdi-arrow-right"
    color="blue"
    :save-text="$t('warehouse.ship')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <warehouse-button-print-menu @print="print" />
    </template>

    <template v-if="editedItem?.id" v-slot:footer-actions>
      <v-btn
        color="blue"
        depressed
        large
        dark
        @click="update"
      >
        <v-icon left>mdi-content-save-edit</v-icon>
        {{ $t('update') }}
      </v-btn>
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="4" xl="2">
          <choose-warehouse-field
            v-model="editedItem.warehouse"
            return-object
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <autocomplete
            v-model="editedItem.dispatched_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.delivered_by')"
            required
            :rules="[rules.required]"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <autocomplete
            v-model="editedItem.received_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.received_by')"
            required
            :rules="[rules.required]"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      waybill
      @removeGrocery="removeGrocery"
    />

    <!-- <v-alert
      v-if="lastStocktakingDate"
      type="warning"
      dense
      text
      class="mt-3"
      icon="mdi-clipboard-alert-outline"
    >
      {{ $t(stocktakingHintKey).replace("{0}", lastStocktakingDate) }}
    </v-alert> -->
  </fullscreen-dialog>
</template>

<script>
import hasLastStocktaking from "@/mixins/warehouse/hasLastStocktaking";
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import { waybillDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehouseWaybillDialog",
  mixins: [hasWarehouseDocumentDialog, hasLastStocktaking, hasPrintingV2],
  props: {
    editedItem: {
      type: Object,
      default: waybillDocumentGrocery,
    },
  },
  methods: {
    update() {
      this.showInfoSnackbar("Ažuriranje otpremnice je još u razvoju. Funkcionalnost će biti dostupna uskoro.");
    },
    print(withStock = false) {
      const {
        row,
        col,
        field,
        text,
        bold,
        group,
        table,
        line,
        spacer,
        heading,
      } = this.printElements;

      const warehouseName = this.toLatin(this.editedItem.warehouse?.name || "");
      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const deliveredByName = this.getUserFullName(this.editedItem.dispatched_by);
      const receivedByName = this.getUserFullName(this.editedItem.received_by);

      this.printDoc(
        [
          // Row 1: warehouse | date
          row("between align-end", [
            field(warehouseName, this.$t("stock"), 300),
            field(docDate, this.$t("date"), 120),
          ]),
          // Row 2: place | delivery method
          spacer(14),
          row("between align-end", [
            field("", this.$t("warehouse.place"), 200),
            field("", this.$t("warehouse.delivery_method"), 200),
          ]),
          // Title
          spacer(14),
          heading(this.$t("warehouse.goods_exit_number") + " " + docNumber),
          // Table
          spacer(10),
          table(this.getPrintHeaders(withStock, false), this.getPrintItems(false)),
          // Signature
          spacer(40),
          row("between", [
            col("center", [
              text(this.$t("warehouse.delivered_by")),
              spacer(16),
              line(),
              text(deliveredByName),
            ]),
            col("center", [
              text(this.$t("warehouse.received_by")),
              spacer(16),
              line(),
              text(receivedByName),
            ]),
          ]),
        ],
        { pageTitle: this.$t("warehouse.goods_exit_number") + " " + docNumber }
      );
    },
    async save() {
      const params = {
        ...this.editedItem,
        document_type: "exit",
        transaction_type: "exit",
        groceries: this.editedItem.groceries.map((grocery) => ({
          grocery_id: grocery.grocery_id || grocery.id,
          warehouse_id: this.editedItem.warehouse.id,
          name: grocery.name,
          code: grocery.code,
          supplier_code: grocery.supplier_code,
          measure: grocery.measure,
          amount: grocery.amount,
          transaction_type: "exit",
        })),
        warehouse_id: this.editedItem.warehouse.id,
      };

      const request = warehouseService.addDocumentTransaction(params);
      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.$t("warehouse.shipping_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
